'use client';

import Footer from '@/src/features/ui/footer';
import FooterSimple from '@/src/features/ui/footer/footer-simple';
import { OriginalLocationContext } from '@/src/store/contexts/original-location-context';
import { usePathname } from 'next/navigation';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import FullSkeleton from '../features/ui/full-skeleton/full-skeleton';
import Header from '../features/ui/header';
import { UserAgent } from '../interfaces/missing-types';
import { DeviceContext } from '../store/contexts/device-context';
import { ResponseMenuItemList } from '@/src/interfaces/data-contracts';
import { Logo } from '../features/ui/icons/icons';
import Link from 'next/link';
import { setCookie } from 'cookies-next';
import Script from 'next/script';
import { isExcluded } from '../utils/is-excluded-path-patterns';
import { generateGUID } from '@/lib/generate-guid';

type Props = {
  children: React.ReactNode;
  userAgent: UserAgent;
  menuList: ResponseMenuItemList;
};

const COOKIE_SESSION = 'session.userInfo';
const COOKIE_TRACKING = 'userTrackingId';

export const LayoutClient: React.FC<Props> = ({ children, userAgent, menuList }) => {
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const pathname = usePathname() ?? '';
  const isBankIntegration = pathname.includes('int/basvuru/form');
  const isPosWizard = pathname.includes('/sihirbaz/form');
  const containsRedirect = pathname.includes('/yonlendirme') && !pathname.includes('success/yonlendirme');
  const { headerItems, hamburgerItems, footerItems } = menuList;
  const containsLoginorForgotPassword =
    (pathname?.includes('/giris') && !pathname.includes('/finansal-skor')) ||
    pathname?.includes('/parola-unuttum') ||
    pathname?.includes('/parola-guncelle');

  useEffect(() => {
    window.scrollTo(0, 0);
    setCookie('referrer', window.location.href);
    // callbackUrl is used to redirect user after they are logged in, hence there are some paths to exclude like /giris
    if (!isExcluded(pathname)) {
      setCookie('session.callbackUrl', window.location.href);
    }
  }, [pathname]);

  useEffect(() => {
    if (!originalUrl) {
      setOriginalUrl(window.location.href);
    }
  }, []);

  const memoizedOriginalUrl = useMemo(() => ({ originalUrl }), [originalUrl]);

  const bootstrapTracking = `
    (function(){
      function read(name){
        const m = document.cookie.match(new RegExp('(?:^|; )'+ name +'=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
      }
      function write(name,val){ document.cookie = name + '=' + encodeURIComponent(val) + '; path=/'; }

      const sessionRaw = read('${COOKIE_SESSION}');
      let hkSha256Email = 'NULL';
      let hkUserTrackingId = '';
      let hkExternalUserId = null;

      if (sessionRaw) {
        try {
          const parsed = JSON.parse(sessionRaw);
          hkSha256Email = parsed._hke || 'NULL';
          hkUserTrackingId = parsed.userTrackingId || '';
          hkExternalUserId = parsed._hkeui || null;
        } catch(e) {
          console.warn('Failed to parse session.userInfo', e);
        }
      }

      const fallbackTracking = read('${COOKIE_TRACKING}');
      if (!hkUserTrackingId && fallbackTracking) {
        hkUserTrackingId = fallbackTracking;
      }

      if (!hkUserTrackingId) {
        hkUserTrackingId = '${generateGUID()}';
      }

      write('${COOKIE_TRACKING}', hkUserTrackingId);

      window.dataLayer = window.dataLayer || [];
      const payload = {};
      if (hkSha256Email && hkSha256Email !== 'NULL') payload.hkSha256Email = hkSha256Email;
      if (hkUserTrackingId) payload.hkUserTrackingId = hkUserTrackingId;
      if (hkExternalUserId) payload.hkExternalUserId = hkExternalUserId;
      window.dataLayer.push(payload);
    })();
  `;

  if (containsRedirect && !isBankIntegration) {
    return (
      <>
        <Script id="bootstrap-tracking" strategy="beforeInteractive">
          {bootstrapTracking}
        </Script>
        <Script id="gtm" strategy="afterInteractive">
          {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
        `}
        </Script>
        <Script src="https://bundles.efilli.com/hangikredikobi.com.prod.js" />
        <DeviceContext.Provider value={userAgent}>
          <OriginalLocationContext.Provider value={memoizedOriginalUrl}>
            {/* footerin ekranin sonuna inmesi isteniyorsa main flex-1 verilmeli */}
            <main className={`block w-full ${isBankIntegration ? 'flex-1' : ''}`}>{children}</main>
            <Suspense fallback={<FullSkeleton />}>
              {/*<DynamicFooterSimple />*/}
              <FooterSimple />
            </Suspense>
          </OriginalLocationContext.Provider>
        </DeviceContext.Provider>
      </>
    );
  }
  if (containsLoginorForgotPassword) {
    return (
      <>
        <Script id="bootstrap-tracking" strategy="beforeInteractive">
          {bootstrapTracking}
        </Script>
        <Script id="gtm" strategy="afterInteractive">
          {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
        `}
        </Script>
        <Script src="https://bundles.efilli.com/hangikredikobi.com.prod.js" />
        <DeviceContext.Provider value={userAgent}>
          <OriginalLocationContext.Provider value={memoizedOriginalUrl}>
            <div className="mt-3 flex flex-col items-center gap-9 md:mt-[42px] md:gap-[46px]">
              <Link href={'/'} data-testid="logoLink">
                <Logo width={127} height={36} data-testid="logo" />
              </Link>
              <main className="flex w-full items-center justify-center">{children}</main>
            </div>
          </OriginalLocationContext.Provider>
        </DeviceContext.Provider>
      </>
    );
  }
  return (
    <>
      <Script id="bootstrap-tracking" strategy="beforeInteractive">
        {bootstrapTracking}
      </Script>
      <Script id="gtm" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
        `}
      </Script>
      <Script src="https://bundles.efilli.com/hangikredikobi.com.prod.js" />
      <DeviceContext.Provider value={userAgent}>
        <OriginalLocationContext.Provider value={memoizedOriginalUrl}>
          <Suspense fallback={<FullSkeleton />}>
            {!(isBankIntegration && containsRedirect) && headerItems && hamburgerItems && !isPosWizard && (
              <Header headerItems={headerItems} hamburgerItems={hamburgerItems} />
            )}
          </Suspense>
          <main className="block w-full flex-1">{children}</main>
          <Suspense fallback={<FullSkeleton />}>
            {/*<DynamicFooter />*/}
            {footerItems && !isPosWizard && <Footer footerItems={footerItems} hideTop={isBankIntegration} />}
          </Suspense>
        </OriginalLocationContext.Provider>
      </DeviceContext.Provider>
    </>
  );
};
